import { useEffect, useState } from "react";
import { Zap, Radio, HeartHandshake, Radar, Briefcase, Heart, MessageSquare, Activity } from "lucide-react";
import { api } from "@/context/AuthContext";

const T = {
  en: {
    title: "Avatar Activity & Credits",
    creditsLeft: "Avatar Chats Remaining", live: "Live Status", online: "LIVE ON RADAR", offline: "OFFLINE",
    matchesToday: "Matches Today", log: "Live Activity Log",
    noEvents: "No activity yet. Your avatar's interactions will stream here.",
    encounter: "Proximity ping with", pitch: "AI pitch exchanged with", match: "Compatibility", withWord: "with",
  },
  es: {
    title: "Actividad del Avatar y Créditos",
    creditsLeft: "Chats de Avatar Restantes", live: "Estado Live", online: "LIVE EN RADAR", offline: "OFFLINE",
    matchesToday: "Matches Hoy", log: "Registro de Actividad en Vivo",
    noEvents: "Sin actividad aún. Las interacciones de tu avatar aparecerán aquí.",
    encounter: "Ping de proximidad con", pitch: "Pitch de IA intercambiado con", match: "Compatibilidad", withWord: "con",
  },
};

const CAT_STYLE = {
  dating: { icon: Heart, cls: "text-pink-400 bg-pink-400/10 border-pink-400/25" },
  business: { icon: Briefcase, cls: "text-[#00F5FF] bg-[#00F5FF]/10 border-[#00F5FF]/25" },
  proximity: { icon: Radar, cls: "text-[#00FFAA] bg-[#00FFAA]/10 border-[#00FFAA]/25" },
  match: { icon: HeartHandshake, cls: "text-[#D080FF] bg-[#B026FF]/10 border-[#B026FF]/25" },
  interaction: { icon: MessageSquare, cls: "text-amber-300 bg-amber-300/10 border-amber-300/25" },
};

export const CreditDashboard = ({ lang, matchOn = true }) => {
  const t = T[lang] || T.en;
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.get("/activity/summary").then((r) => setData(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  if (!data) return null;
  const pct = data.credits_total ? Math.round((data.credits_left / data.credits_total) * 100) : 0;

  const eventLine = (e) => {
    if (e.type === "match_score") return `${t.match} ${e.compatibility}% ${t.withWord} ${e.other_name}`;
    if (e.type === "pitch") return `${t.pitch} ${e.other_name}`;
    return `${t.encounter} ${e.other_name}${e.distance != null ? ` (${e.distance}m)` : ""}`;
  };

  return (
    <div className="mt-8" data-testid="credit-dashboard">
      <h2 className="font-heading text-sm font-black uppercase tracking-[0.2em] text-white/60 mb-4 flex items-center gap-2">
        <Activity size={15} className="text-[#00F5FF]" /> {t.title}
      </h2>
      <div className={`grid grid-cols-1 ${matchOn ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-5 mb-6`}>
        <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-6" data-testid="credits-card">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2 flex items-center gap-1.5"><Zap size={12} className="text-[#00FFAA]" /> {t.creditsLeft}</p>
          <p className="font-heading text-3xl font-black text-white" data-testid="credits-left">{data.credits_left}<span className="text-sm text-white/30 font-bold"> / {data.credits_total}</span></p>
          <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#00FFAA] to-[#00F5FF] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-6" data-testid="live-status-card">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2 flex items-center gap-1.5"><Radio size={12} className="text-[#B026FF]" /> {t.live}</p>
          <span className={`inline-flex items-center gap-2 mt-2 text-[11px] font-black tracking-widest px-4 py-2 rounded-full ${
            data.live ? "bg-[#00FFAA]/10 text-[#00FFAA] border border-[#00FFAA]/40" : "bg-white/5 text-white/40 border border-white/10"
          }`}>
            <span className={`w-2 h-2 rounded-full ${data.live ? "bg-[#00FFAA] animate-pulse" : "bg-white/30"}`} />
            {data.live ? t.online : t.offline}
          </span>
        </div>
        {matchOn && (
        <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-6" data-testid="matches-today-card">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2 flex items-center gap-1.5"><HeartHandshake size={12} className="text-[#D080FF]" /> {t.matchesToday}</p>
          <p className="font-heading text-3xl font-black text-[#D080FF]" data-testid="matches-today-count">{data.matches_today}</p>
        </div>
        )}
      </div>

      <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-6" data-testid="activity-log">
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-4">{t.log}</p>
        {data.events.length === 0 && <p className="text-xs text-white/30" data-testid="activity-empty">{t.noEvents}</p>}
        <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
          {data.events.map((e) => {
            const s = CAT_STYLE[e.category] || CAT_STYLE.interaction;
            const Icon = s.icon;
            return (
              <div key={e.id} className="flex items-center gap-3 bg-white/[0.03] border border-white/5 rounded-xl px-4 py-3" data-testid="activity-event-row">
                <span className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${s.cls}`}><Icon size={14} /></span>
                <p className="text-xs text-white/80 flex-1 min-w-0 truncate">{eventLine(e)}</p>
                <p className="text-[10px] text-white/30 shrink-0">{new Date(e.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
